import React, { useMemo } from "react";
import { useLang } from "@/context/LanguageContext";
import { fmtNumber } from "@/lib/i18n";
import AgeDonut from "@/components/AgeDonut";

export function Stat({ label, value, small, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">{label}</div>
      <div className={`stat-number text-ink ${small ? "text-base leading-snug" : "text-2xl"}`}>{value}</div>
    </div>
  );
}

export function Section({ title, children }) {
  return (
    <section className="px-6 py-4 border-t border-stoneLine/60">
      <h3 className="text-[10px] uppercase tracking-[0.18em] text-ochre mb-3 font-semibold">{title}</h3>
      {children}
    </section>
  );
}

const Label = ({ children }) => <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">{children}</div>;
const loc = (field, lang) => field?.[lang] || field?.en;

export function Demographics({ country }) {
  const { lang, t } = useLang();
  const ageLabels = useMemo(() => [t("age_0_14"), t("age_15_29"), t("age_30_59"), t("age_60plus")], [t]);
  if (!country.largest_city && !country.age_distribution) return null;
  return (
    <Section title={t("demographics")}>
      {country.largest_city && (
        <div className="mb-4" data-testid="panel-largest-city">
          <Label>{t("largest_city")}</Label>
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="stat-number text-lg text-ink">{loc(country.largest_city.name, lang)}</span>
            <span className="text-sm text-muted-foreground">≈ {fmtNumber(country.largest_city.population, lang)} {t("inhabitants")}</span>
          </div>
        </div>
      )}
      {country.age_distribution && (
        <div data-testid="panel-age-distribution">
          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">{t("age_distribution")} · {country.age_distribution.year}</div>
          <AgeDonut
            data={country.age_distribution}
            labels={ageLabels}
            fmt={(v) => fmtNumber(Math.round(v * 10) / 10, lang)}
          />
          <div className="text-[10px] text-muted-foreground mt-2">{t("source_wb")}</div>
        </div>
      )}
    </Section>
  );
}

export function History({ country }) {
  const { lang, t } = useLang();
  if (!country.historical_note) return null;
  return (
    <Section title={t("history")}>
      <div className="mb-3" data-testid="panel-independence">
        <Label>{t("independence")}</Label>
        <div className="stat-number text-lg leading-snug text-ochre">{loc(country.independence, lang)}</div>
      </div>
      <div className="grid grid-cols-2 gap-4 mb-3">
        <div data-testid="panel-industrial-city">
          <Label>{t("industrial_city")}</Label>
          <div className="text-sm text-ink">{loc(country.industrial_city, lang)}</div>
        </div>
        <div data-testid="panel-religious-city">
          <Label>{t("religious_city")}</Label>
          <div className="text-sm text-ink">{loc(country.religious_city, lang)}</div>
        </div>
      </div>
      <p className="text-sm leading-relaxed text-ink/85 border-s-2 border-ochre/40 ps-3" data-testid="panel-historical-note">
        {loc(country.historical_note, lang)}
      </p>
    </Section>
  );
}

export function Borders({ country, codeToName, onNavigate }) {
  const { t } = useLang();
  return (
    <Section title={t("borders")}>
      {country.borders.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("no_borders")}</p>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="panel-borders">
          {country.borders.map((code) => (codeToName[code] ? (
            <button key={code} onClick={() => onNavigate(code)} data-testid={`border-link-${code}`}
              className="text-xs px-2.5 py-1 rounded-full bg-sandDeep/60 border border-stoneLine hover:bg-ochre/15 hover:border-ochre hover:text-ochre transition-colors">
              {codeToName[code]}
            </button>
          ) : (
            <span key={code} className="text-xs px-2.5 py-1 rounded-full bg-sandDeep/40 border border-stoneLine text-ink/70">{code}</span>
          )))}
        </div>
      )}
    </Section>
  );
}
